import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { AdfsAuthProperties } from 'server/shared/helpers/types/AuthProperties';
import { CreateOptInType } from 'server/shared/models/optIn';
import { SendEnrollEmailPayload, User } from 'server/shared/models/user';
import { ToastService } from './toast/toast.service';

export interface AuthProperties {
  user: { email: string };
  password: string;
  twoFactorAuthCode?: number;
}

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  twoFactorAuthRequired = new BehaviorSubject<boolean>(false);
  invalidAuthCode = new BehaviorSubject<boolean>(false);
  isLoading = new BehaviorSubject<boolean>(false);
  phoneNumber = new BehaviorSubject<string>('');
  email = new BehaviorSubject<string>('');
  constructor(
    private router: Router,
    private toastService: ToastService,
    private ngZone: NgZone,
  ) {}

  setStorageLoginEmail(email: string) {
    localStorage.setItem('loginEmail', email);
  }

  getStorageLoginEmail() {
    return localStorage.getItem('loginEmail');
  }

  removeStorageLoginEmail() {
    localStorage.removeItem('loginEmail');
  }

  loginWithMsal(
    tokens: { accessToken: string; idToken: string },
    email: string,
  ) {
    this.isLoading.next(true);
    // @ts-ignore
    Accounts.callLoginMethod({
      methodArguments: [
        {
          msal: tokens,
          email: email,
        },
      ],
      userCallback: (error: Meteor.Error) => {
        this.ngZone.run(() => {
          if (error) {
            this.handleMsalError(error);
          }
          this.isLoading.next(false);
        });
      },
    });
  }

  loginWithAdfs(code: string, email: string) {
    this.isLoading.next(true);
    const args: AdfsAuthProperties = {
      adfs: {
        code,
      },
      email: email,
      redirectUri: window.location.origin + '/',
    };
    // @ts-ignore
    Accounts.callLoginMethod({
      methodArguments: [args],
      userCallback: (error: Meteor.Error) => {
        this.ngZone.run(() => {
          if (error) {
            this.handleAdfsError(error);
          }
          this.isLoading.next(false);
        });
      },
    });
  }

  loginWithDemoInit(email: string, callback?: (error: Meteor.Error) => void) {
    this.isLoading.next(true);
    // @ts-ignore
    Accounts.callLoginMethod({
      methodArguments: [{ user: { email }, demoInit: true }],
      userCallback: (error: Meteor.Error) => {
        this.isLoading.next(false);
        if (callback) {
          callback(error);
        }
      },
    });
  }

  loginMethod(
    authData?: AuthProperties,
    resendAuthCode?: boolean,
    isPasswordReset?: boolean,
  ) {
    this.isLoading.next(true);
    // @ts-ignore
    Accounts.callLoginMethod({
      methodArguments: [
        {
          user: authData?.user,
          password: authData?.password,
        },
        { twoFactorAuthCode: authData?.twoFactorAuthCode },
      ],
      userCallback: (error: Meteor.Error) => {
        this.ngZone.run(() => {
          if (error) {
            this.handleAuthError(error);
          } else {
            if (isPasswordReset) {
              this.router
                .navigate(['/dashboard'])
                .then(() => window.location.reload());
            }
          }
          this.isLoading.next(false);
        });
      },
    });
  }

  handleMsalError(error: Meteor.Error) {
    console.error(error);
    if (error.message === 'No user found for this MSAL login [401]') {
      this.toastService.add({
        type: 'ERROR',
        message: 'noUserConnectedWithMicrosoft',
      });
      return;
    } else if (error.message === 'TokenExpiredError: jwt expired [401]') {
      this.toastService.add({
        type: 'ERROR',
        message: 'loginExpired',
      });
    } else if (error.message === 'missingIdToken') {
      this.isLoading.next(false);
      return;
    } else {
      this.toastService.add({
        type: 'ERROR',
        message: 'msalTokenError',
      });
    }
  }

  handleAdfsError(error: Meteor.Error) {
    console.error(error);
    if (error.message === 'No user found for this ADFS login [401]') {
      this.toastService.add({
        type: 'ERROR',
        message: 'noUserConnectedWithAdfs',
      });
      return;
    } else if (error.message === 'TokenExpiredError: jwt expired [401]') {
      this.toastService.add({
        type: 'ERROR',
        message: 'loginExpired',
      });
    } else if (error.message === 'missingIdToken') {
      this.isLoading.next(false);
      return;
    } else {
      this.toastService.add({
        type: 'ERROR',
        message: 'adfsTokenError',
      });
    }
  }

  handleAuthError(error: Meteor.Error, resendAuthCode?: boolean) {
    if (error.reason == 'demoExpired') {
      this.toastService.add({
        type: 'ERROR',
        message: 'demoExpired',
      });
    } else if (error.reason == 'loginForbidden') {
      this.toastService.add({
        type: 'ERROR',
        message: 'loginForbidden',
      });
    } else if (error.reason === 'phoneNotFound') {
      this.toastService.add({
        type: 'INFO',
        message: 'contactSupport',
      });
      this.toastService.add({
        type: 'ERROR',
        message: 'phoneNotFound',
      });
    } else if (error.reason === 'phoneNotVerified') {
      this.toastService.add({
        type: 'INFO',
        message: 'optInPhoneMessage',
        timeout: 10000,
      });
      this.toastService.add({
        type: 'ERROR',
        message: 'phoneNotVerified',
        timeout: 10000,
      });
    } else if (error.reason === 'twoFactorAuthRequiredPhone') {
      if (resendAuthCode) {
        this.toastService.add({
          type: 'INFO',
          message: 'authCodeResent',
        });
      }
      this.phoneNumber.next(error.details || '');
      this.twoFactorAuthRequired.next(true);
    } else if (error.reason === 'twoFactorAuthRequiredEmail') {
      if (resendAuthCode) {
        this.toastService.add({
          type: 'INFO',
          message: 'authCodeResent',
        });
      }
      this.email.next(error.details || '');
      this.twoFactorAuthRequired.next(true);
    } else if (error.reason === 'authCodeWrong') {
      this.invalidAuthCode.next(true);
    } else {
      this.toastService.add({
        type: 'INFO',
        message: 'usernameOrPasswordWrong',
      });
    }
  }

  sendPhoneConfirmationSMS(user: User, phoneNr: string) {
    let options: CreateOptInType = {
      type: 'SMS',
      value: phoneNr,
      userId: user._id,
    };
    this.createOptIn(options);
  }

  sendEmailConfirmation(user: User) {
    let options: CreateOptInType = {
      type: 'EMAIL',
      value: user.email,
      userId: user._id,
    };
    this.createOptIn(options);
  }

  async sendEnrollSMS(user: User): Promise<any> {
    return await new Promise((resolve, reject) => {
      Meteor.call('sendEnrollSms', user, (error: Meteor.Error, result: any) => {
        if (error) {
          this.toastService.makeServerErrorToast();
          reject(error);
        } else {
          // silent success on phone removal because there is no optin created
          if (user.phone) {
            this.toastService.add({
              type: 'INFO',
              message: 'optInPhoneMessage',
              timeout: 5000,
            });
          }
          resolve(result);
        }
      });
    });
  }

  async sendEnrollEmail(user: User, customerId: string): Promise<any> {
    let options: SendEnrollEmailPayload = {
      userId: user._id,
      customerId: customerId,
      email: user.email,
    };
    return await new Promise((resolve, reject) => {
      Meteor.call(
        'sendEnrollEmail',
        options,
        (error: Meteor.Error, result: any) => {
          if (error) {
            this.toastService.makeServerErrorToast();
            reject(result);
          } else if (result === false) {
            this.toastService.add({
              type: 'ERROR',
              message: 'emailNotchanged',
              timeout: 10000,
            });
            reject('User or customer could not be found');
          } else {
            this.toastService.add({
              type: 'INFO',
              message: 'optInEmailMessage',
              timeout: 5000,
            });
            resolve(result);
          }
        },
      );
    });
  }

  createOptIn(options: CreateOptInType) {
    Meteor.call('createOptIn', options, (error: Meteor.Error, result: any) => {
      if (error) {
        this.toastService.makeServerErrorToast();
        return;
      }
      if (result?.existsAlready) {
        this.toastService.add({
          type: 'ERROR',
          message: 'optInEmailExists',
          timeout: 10000,
        });
      } else {
        if (options.type === 'EMAIL') {
          this.toastService.add({
            type: 'INFO',
            message: 'optInEmailMessage',
            timeout: 10000,
          });
        } else {
          // silent success when removing phone
          this.toastService.add({
            type: 'SUCCESS',
            message: 'optInPhoneMessage',
            timeout: 10000,
          });
        }
      }
    });
  }
}
